import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import socketManager from '../../utils/SocketManager';
import "./scss/ExportImport.scss";
import { config } from '../../config';
import Spinner from '../Global/Spinner';
import MemberInvitation from './MemberInvitation';
import { useNavigate } from 'react-router-dom';

import { fetchUser } from '../../redux/Slices/thunks';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import { hideSnackBar } from '../../redux/Slices/snackbarSlice';
import { api } from '../../utils/apiUtils';


const ImportProgress = () => {
    const [log, setLog] = useState({});
    const [progress, setProgress] = useState(0);
    const [importSuccess, setImportSuccess] = useState(false);
    const [isImportStart, setImportStart] = useState(false);
    const [showMemberInvitation, setShowMemberInvitation] = useState(false);
    const [memberInviteOptions, setMemberInviteOptions] = useState([]);
    const [importedBoardId, setImportedBoardId] = useState(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user } = useSelector(state => state.user);
    const jobId = useRef(localStorage.getItem('activeImportJobId'));
    const pollRef = useRef(null);

    useEffect(() => {
        if (!user) {
            dispatch(fetchUser());
        }
    }, [dispatch, user]);

    // Handle completion (shared by socket and poll)
    const handleComplete = useCallback((data) => {
        setImportSuccess(true);
        setProgress(100);
        localStorage.removeItem('activeImportJobId');

        dispatch(fetchWorkspaces());

        if (data?.boardId) {
            setImportedBoardId(data.boardId);
        }

        if (data?.memberInviteOptions && data.memberInviteOptions.length > 0) {
            setMemberInviteOptions(data.memberInviteOptions);
            setShowMemberInvitation(true);
        }
    }, [dispatch]);

    // Polling fallback — fetch job status from API
    useEffect(() => {
        if (!jobId.current || importSuccess) return;

        const poll = async () => {
            try {
                const job = await api.get(`${config.API_URI}/v1/import/jobs/${jobId.current}`);
                if (!job) return;

                if (job.status === 'processing' || job.status === 'pending') {
                    setImportStart(true);
                    const pct = job.totalItems > 0 ? (job.processedItems / job.totalItems) * 100 : 0;
                    setProgress(pct);
                    setLog({
                        title: job.currentItemTitle || `Importing ${job.currentPhase || ''}...`,
                        index: job.processedItems,
                        length: job.totalItems,
                    });
                } else if (job.status === 'completed') {
                    setLog({ index: job.totalItems, length: job.totalItems });
                    handleComplete({
                        memberInviteOptions: job.memberInviteOptions || [],
                        boardId: job.boardId,
                    });
                    if (pollRef.current) clearInterval(pollRef.current);
                } else if (job.status === 'failed') {
                    if (pollRef.current) clearInterval(pollRef.current);
                    localStorage.removeItem('activeImportJobId');
                    const errorMsg = job.errors?.length > 0 ? job.errors[job.errors.length - 1].message : 'Unknown error';
                    alert(`Import failed: ${errorMsg}`);
                    navigate('/import');
                }
            } catch (err) {
                console.error('Poll error:', err);
            }
        };

        // Poll immediately once, then every 3 seconds
        poll();
        pollRef.current = setInterval(poll, 3000);

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [importSuccess, handleComplete, navigate]);

    // Live import updates via the shared SocketManager connection (join handled
    // centrally). We subscribe/unsubscribe rather than open/disconnect our own
    // socket — disconnecting would tear down the app-wide connection.
    useEffect(() => {
        if (!user) return;

        const handleProgress = (data) => {
            setImportStart(true);
            dispatch(hideSnackBar());
            setProgress(data.progress);
            setLog(data);

            if (data.progress >= 100 && data.completed) {
                handleComplete({});
            }
        };

        const handleImportComplete = (data) => {
            handleComplete(data);
        };

        const handleImportError = (data) => {
            console.error('Import error:', data);
            localStorage.removeItem('activeImportJobId');
            if (pollRef.current) clearInterval(pollRef.current);
            alert(`Import failed: ${data.message}`);
            navigate('/import');
        };

        const unsubProgress = socketManager.on('importProgress', handleProgress);
        const unsubComplete = socketManager.on('importComplete', handleImportComplete);
        const unsubError = socketManager.on('importError', handleImportError);

        return () => {
            unsubProgress();
            unsubComplete();
            unsubError();
        };
    }, [user, dispatch, handleComplete, navigate]);

    const handleInvitationComplete = () => {
        setShowMemberInvitation(false);
        navigate(importedBoardId ? `/b/${importedBoardId}` : '/');
    };

    const handleSkipInvitation = () => {
        setShowMemberInvitation(false);
        navigate(importedBoardId ? `/b/${importedBoardId}` : '/');
    };

    if (showMemberInvitation && memberInviteOptions.length > 0) {
        return (
            <MemberInvitation
                members={memberInviteOptions}
                boardId={importedBoardId}
                onComplete={handleInvitationComplete}
                onSkip={handleSkipInvitation}
            />
        );
    }

    return (
        <div>
            <div className="export-import-container">

                {progress !== '0' ? (
                    <div className="progress-bar-container">
                        <div className="progress-bar-header">
                            <div className={`import-titles${importSuccess ? ' import-success' : ''}`}>
                                <div className="icon-bar">
                                    {importSuccess ? (
                                        <span className="material-symbols-outlined success-icon">
                                            check_circle
                                        </span>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined import-icon">import_export</span>
                                            {!importSuccess && <Spinner size={40} color="#3498db" speed={1.5} />}
                                        </>
                                    )}
                                </div>

                                <span className='title'>
                                    {importSuccess ? 'Successfully Imported' : log.title || 'Importing...'}
                                </span>
                            </div>
                            <div className="items-imported">
                                {log.index || 0} / {log.length || 0}
                            </div>
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-bar-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <div className="progress-bar-percentage">{typeof progress === 'number' ? progress.toFixed(0) : 0}%</div>

                        {importSuccess && (
                            <div className="import-success-actions">
                                {importedBoardId ? (
                                    <button
                                        className="go-to-board-btn"
                                        onClick={() => navigate(`/b/${importedBoardId}`)}
                                    >
                                        Go to Board
                                    </button>
                                ) : (
                                    <button
                                        className="go-to-board-btn"
                                        onClick={() => navigate('/')}
                                    >
                                        Go to Home
                                    </button>
                                )}
                                <button
                                    className="import-another-btn"
                                    onClick={() => navigate('/import')}
                                >
                                    Import Another
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="no-progress-message">
                        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#888"><path d="M440-280h80v-240h-80zm40-320q17 0 28.5-11.5T520-640t-11.5-28.5T480-680t-28.5 11.5T440-640t11.5 28.5T480-600m0 520q-83 0-156-31.5T197-197t-85.5-127T80-480t31.5-156T197-763t127-85.5T480-880t156 31.5T763-763t85.5 127T880-480t-31.5 156T763-197t-127 85.5T480-80m0-80q134 0 227-93t93-227-93-227-227-93-227 93-93 227 93 227 227 93m0-320" /></svg>
                        <span className="title">No import progress yet. Please wait or start an import.</span>
                    </div>
                )}
            </div>

        </div>
    );
}

export default ImportProgress;
